'use client';

import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { useEffect, useState } from 'react';

type MediaCarouselProps<Item> = {
  ariaLabel: string;
  carouselClassName?: string;
  carouselContentClassName?: string;
  carouselOptions?: React.ComponentProps<typeof Carousel>['opts'];
  desktopNextButtonClassName: string;
  desktopPreviousButtonClassName: string;
  dotClassName: (isSelected: boolean) => string;
  dotDataTest: (index: number) => string;
  itemKey: (item: Item, index: number) => string;
  items: Item[];
  nextButtonDataTest: string;
  previousButtonDataTest: string;
  renderItem: (item: Item, index: number) => React.ReactNode;
  slideClassName: string;
};

export const MediaCarousel = <Item extends object>({
  ariaLabel,
  carouselClassName = '',
  carouselContentClassName = '',
  carouselOptions,
  desktopNextButtonClassName,
  desktopPreviousButtonClassName,
  dotClassName,
  dotDataTest,
  itemKey,
  items,
  nextButtonDataTest,
  previousButtonDataTest,
  renderItem,
  slideClassName,
}: Readonly<MediaCarouselProps<Item>>): React.JSX.Element => {
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;

    const updateCarouselState = (): void => {
      setCanScrollNext(carouselApi.canScrollNext());
      setCanScrollPrev(carouselApi.canScrollPrev());
      setSelectedIndex(carouselApi.selectedScrollSnap());
    };
    updateCarouselState();
    carouselApi.on('reInit', updateCarouselState);
    carouselApi.on('select', updateCarouselState);

    return (): void => {
      carouselApi.off('reInit', updateCarouselState);
      carouselApi.off('select', updateCarouselState);
    };
  }, [carouselApi]);

  return (
    <Carousel aria-label={ariaLabel}
      className={carouselClassName}
      opts={carouselOptions}
      setApi={setCarouselApi}
    >
      <CarouselContent className={`${carouselContentClassName} ${items.length === 1 ? 'justify-center' : ''}`}>
        {items.map((item, index) =>
          <CarouselItem key={itemKey(item, index)}
            aria-label={`Slide ${index + 1} of ${items.length}`}
            className={slideClassName}
          >
            {renderItem(item, index)}
          </CarouselItem>
        )}
      </CarouselContent>

      {items.length > 1 &&
        <>
          {canScrollPrev &&
            <CarouselPrevious className={desktopPreviousButtonClassName}
              dataTest={previousButtonDataTest}
              iconFontSizeClassName="size-8"
              variant="secondary"
            />
          }
          {canScrollNext &&
            <CarouselNext className={desktopNextButtonClassName}
              dataTest={nextButtonDataTest}
              iconFontSizeClassName="size-8"
              variant="secondary"
            />
          }
          <div aria-hidden="true"
            className="absolute bottom-sm left-1/2 flex -translate-x-1/2 gap-sm md:hidden"
          >
            {items.map((_, index) =>
              <span key={dotDataTest(index)}
                className={dotClassName(selectedIndex === index)}
                data-test={dotDataTest(index)}
              />
            )}
          </div>
        </>
      }
    </Carousel>
  );
};
